import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// v2: earlier builds stored the OS-derived mode; a new key resets everyone to light once
const STORAGE_KEY = 'gswm-theme-mode-v2';

const readInitialMode = () => {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (e) {
    // storage blocked (private mode) — fall back to the default
  }
  // Light by default; dark only when the user picks it (not from the OS setting)
  return 'light';
};

const ThemeModeContext = createContext({ mode: 'light', toggleMode: () => {}, setMode: () => {} });

// Light/dark mode for the whole app. The mode is mirrored to <html data-theme="...">
// so plain CSS (assets/css/modern.css) can switch its CSS variables.
export const ThemeModeProvider = ({ children }) => {
  const [mode, setMode] = useState(readInitialMode);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', mode);
    root.style.colorScheme = mode;
  }, [mode]);

  // Persist only explicit user choices
  const chooseMode = useCallback((next) => {
    setMode(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // ignore
    }
  }, []);
  const toggleMode = useCallback(
    () => chooseMode(mode === 'dark' ? 'light' : 'dark'),
    [mode, chooseMode]
  );
  const value = useMemo(
    () => ({ mode, setMode: chooseMode, toggleMode }),
    [mode, chooseMode, toggleMode]
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = () => useContext(ThemeModeContext);
